import { Modal, ModalBody, ModalHeader } from 'reactstrap'
import { images } from 'assets'
import { Form, Formik } from 'formik'
import { database, databaseRef } from 'utils/firebase'
import { push, update } from 'firebase/database'
import { useAlert } from 'react-alert'
import { useEffect, useState, useRef } from 'react'
import { useSelector } from 'react-redux'
import { labRequisitionTemplateSchema } from 'validationSchema'
import styles from '../../../styles.module.scss'
import CustomCheckbox from 'components/CustomInputs/CustomCheckbox'
import CustomInput from 'components/CustomInputs/CustomInput'
import CustomTextArea from 'components/CustomInputs/CustomTextArea'
import { addDoctorLog } from 'utils/functions'

const AddModal = ({ isOpen, closeModal, edit, editKey, templates }) => {
  const alert = useAlert()
  const formikRef = useRef()
  const [options, setOptions] = useState({ showTest: false })
  const [searchValue, setSearchValue] = useState('')
  const [initialValues, setInitialValues] = useState({
    name: '',
    tests: '',
    biochemistry: [],
    viralHepatisis: [],
    hematology: [],
    immunology: [],
    prostate: [],
    microbiology: [],
    chlamydia: '',
    gonorrhea: '',
    wound: '',
    other: '',
    vitamind: [],
    reason: '',
  })
  const { me } = useSelector((state) => state.app)

  useEffect(() => {
    if (edit && editKey && templates) {
      const selectedTemplate = templates[editKey]
      if (selectedTemplate) {
        setInitialValues({
          ...selectedTemplate.data,
          name: selectedTemplate.name,
        })
        if (formikRef.current) {
          formikRef.current.setFieldValue(
            'isScreening',
            selectedTemplate.data.isScreening,
          )
          if (selectedTemplate.data.isScreening === 'Yes') {
            formikRef.current.setFieldValue(
              'screeningInterval',
              selectedTemplate.data.screeningInterval,
            )
          } else {
            formikRef.current.setFieldValue('screeningInterval', '')
          }
        }
        setOptions({ showTest: true })
      }
    } else {
      setInitialValues({
        name: '',
        tests: '',
        biochemistry: [],
        viralHepatisis: [],
        hematology: [],
        immunology: [],
        prostate: [],
        microbiology: [],
        chlamydia: '',
        gonorrhea: '',
        wound: '',
        other: '',
        vitamind: [],
        reason: '',
      })
    }
  }, [edit, editKey, templates])

  const handleSubmit = async (values, action) => {
    action.resetForm({ dirtyFields: {} })

    const formattedValues = {
      ...values,
    }
    const updates = {}
    try {
      if (edit && editKey) {
        updates[`templates/lab/${editKey}/data`] = formattedValues
        updates[`templates/lab/${editKey}/name`] = formattedValues?.name

        await update(databaseRef(database), updates)

        addDoctorLog({
          action: 'UPDATE_LAB_TEMPLATE',
          category: 'template',
          description: `Updated lab template: ${formattedValues.name}`,
          doctorID: me.id,
          details: {
            templateId: editKey,
            templateName: formattedValues.name,
            timestamp: Date.now()
          }
        })
      } else {
        const labRef = databaseRef(database, 'templates/lab')
        const newRef = push(labRef)
        const labKey = newRef.key

        updates[`templates/lab/${labKey}/data`] = formattedValues
        updates[`templates/lab/${labKey}/name`] = formattedValues?.name

        await update(databaseRef(database), updates)

        addDoctorLog({
          action: 'CREATE_LAB_TEMPLATE',
          category: 'template',
          description: `Created new lab template: ${formattedValues.name}`,
          doctorID: me.id,
          details: {
            templateId: labKey,
            templateName: formattedValues.name,
            timestamp: Date.now()
          }
        })
      }

      alert.success('Data saved successfully')
      closeModal()
    } catch (error) {
      console.error('Error saving template:', error)
      alert.error('Failed to save template')
    }
  }
  const resetForm = () => {
    if (formikRef.current) {
      formikRef.current.resetForm()
      alert.success('Form cleared')
    } else {
      alert.error('Unable to reset form')
    }
  }

  const searchStringChecker = (string) => {
    if (!string) {
      return true
    }
    const lowercaseString = string.toLowerCase()
    const lowercaseSearchValue = searchValue.toLowerCase()
    return lowercaseString.includes(lowercaseSearchValue)
  }

  return (
    <Modal isOpen={isOpen} centered size="lg">
      <ModalHeader toggle={closeModal}>
        <span className={styles.title}>
          {edit ? 'Edit' : 'New'} Lab Requisition
        </span>
      </ModalHeader>
      <ModalBody>
        <div>
          <div className="d-flex justify-content-between">
            <span>Lab Requisitions :</span>
            {!edit && (
              <button
                className={styles.clearbtn}
                onClick={resetForm}
                type="button"
              >
                Clear form
              </button>
            )}
          </div>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={labRequisitionTemplateSchema}
            innerRef={formikRef}
          >
            {(FormikProps) => (
              <Form className={styles.requisitionForm}>
                <div className="mb-4">
                  <CustomInput
                    name="name"
                    label="Name"
                    placeholder="Enter name"
                  />
                </div>

                {searchStringChecker('BIOCHEMISTERY') && (
                  <div className={styles.CheckboxesDiv}>
                    <span className={styles.title}>BIOCHEMISTERY</span>
                    <div className={styles.innerCheckboxes}>
                      <CustomCheckbox
                        name="biochemistry"
                        label="Albumin"
                        value="Albumin"
                      />
                      <CustomCheckbox
                        name="biochemistry"
                        label="Albumin/Creatine Ratio"
                        value="Albumin/Creatine Ratio"
                      />
                      <CustomCheckbox
                        name="biochemistry"
                        label="Alk. Phosphatase"
                        value="Alk. Phosphatase"
                      />
                      <CustomCheckbox
                        name="biochemistry"
                        label="ALT"
                        value="ALT"
                      />
                      <CustomCheckbox
                        name="biochemistry"
                        label="Bilirubin"
                        value="Bilirubin"
                      />
                      <CustomCheckbox
                        name="biochemistry"
                        label="Creatinine(eGFR)"
                        value="Creatinine(eGRF)"
                      />
                      <CustomCheckbox
                        name="biochemistry"
                        label="Glucose (Fasting)"
                        value="Glucose (Fasting)"
                      />
                      <CustomCheckbox
                        name="biochemistry"
                        label="Glucose (Random)"
                        value="Glucose (Random)"
                      />
                      <CustomCheckbox
                        name="biochemistry"
                        label="HbA1C"
                        value="HbA1C"
                      />
                      <CustomCheckbox
                        name="biochemistry"
                        label="Lipid Assessment (includes Cholesterol, HDL-C, Triglycerides, calculated LDL-C and Chol/HDL-C ratio)"
                        value="Lipid Assessment (includes Cholesterol, HDL-C, Triglycerides, calculated LDL-C and Chol/HDL-C ratio)"
                      />
                      <CustomCheckbox
                        name="biochemistry"
                        label="Neonatal Bilirubin"
                        value="Neonatal Bilirubin"
                      />
                      <CustomCheckbox
                        name="biochemistry"
                        label="Potassium"
                        value="Potassium"
                      />
                      <CustomCheckbox
                        name="biochemistry"
                        label="Sodium"
                        value="Sodium"
                      />
                      <CustomCheckbox
                        name="biochemistry"
                        label="Uric Acid"
                        value="Uric Acid"
                      />
                      <CustomCheckbox
                        name="biochemistry"
                        label="Urinalysis (Chemical)"
                        value="Urinalysis (Chemical)"
                      />
                    </div>
                  </div>
                )}

                {searchStringChecker('VIRAL HEPATITIS') && (
                  <div className={styles.CheckboxesDiv}>
                    <span className={styles.title}>VIRAL HEPATITIS</span>
                    <div className={styles.innerCheckboxes}>
                      <CustomCheckbox
                        name="viralHepatisis"
                        label="Acute hepatitis"
                        value="Acute hepatitis"
                      />
                      <CustomCheckbox
                        name="viralHepatisis"
                        label="Chronic hepatitis"
                        value="Chronic hepatitis"
                      />
                      <CustomCheckbox
                        name="viralHepatisis"
                        label="Immune status /prev. exposure Hepatitis A"
                        value="Immune status /prev. exposure Hepatitis A"
                      />
                      <CustomCheckbox
                        name="viralHepatisis"
                        label="Immune status /prev. exposure Hepatitis B"
                        value="Immune status /prev. exposure Hepatitis B"
                      />
                      <CustomCheckbox
                        name="viralHepatisis"
                        label="Immune status /prev. exposure Hepatitis C"
                        value="Immune status /prev. exposure Hepatitis C"
                      />
                    </div>
                  </div>
                )}
                {searchStringChecker('HEMATOLOGY') && (
                  <div className={styles.CheckboxesDiv}>
                    <span className={styles.title}>HEMATOLOGY</span>
                    <div className={styles.innerCheckboxes}>
                      <CustomCheckbox
                        name="hematology"
                        label="Complete Blood Count(CBC)"
                        value="Complete Blood Count(CBC)"
                      />
                      <CustomCheckbox
                        name="hematology"
                        label="Prothrombin Time (INR)"
                        value="Prothrombin Time (INR)"
                      />
                    </div>
                  </div>
                )}
                {searchStringChecker('IMMUNOLOGY') && (
                  <div className={styles.CheckboxesDiv}>
                    <span className={styles.title}>IMMUNOLOGY</span>
                    <div className={styles.innerCheckboxes}>
                      <CustomCheckbox
                        name="immunology"
                        label="Mononucleosis Screen"
                        value="Mononucleosis Screen"
                      />
                      <CustomCheckbox
                        name="immunology"
                        label="Pregnancy test (Urine)"
                        value="Pregnancy test (Urine)"
                      />
                      <CustomCheckbox
                        name="immunology"
                        label="Prenatal: ABO. RhD. antibody"
                        value="Prenatal: ABO. RhD. antibody"
                      />
                      <CustomCheckbox
                        name="immunology"
                        label="Repeat Prenatal antibodies"
                        value="Repeat Prenatal antibodies"
                      />
                      <CustomCheckbox
                        name="immunology"
                        label="Rubella"
                        value="Rubella"
                      />
                    </div>
                  </div>
                )}
                {searchStringChecker('Prostate Specific Antigen(PSA)') && (
                  <div className={styles.CheckboxesDiv}>
                    <span className={styles.title}>
                      Prostate Specific Antigen(PSA)
                    </span>
                    <span className={styles.light}>
                      if the doctor suspects prostate cancer, then patient is
                      PSA insured. Else they are not PSA insured and is out of
                      pocket payment.
                    </span>
                    <div className={styles.innerCheckboxes}>
                      <CustomCheckbox
                        name="prostate"
                        label="Total PSA Insured"
                        value="Total PSA Insured"
                      />
                      <CustomCheckbox
                        name="prostate"
                        label="Total PSA Uninsured"
                        value="Total PSA Uninsured"
                      />
                      <CustomCheckbox
                        name="prostate"
                        label="Free PSA Insured"
                        value="Free PSA Insured"
                      />
                      <CustomCheckbox
                        name="prostate"
                        label="Free PSA Uninsured"
                        value="Free PSA Uninsured"
                      />
                    </div>
                  </div>
                )}
                {searchStringChecker('MICROBIOLOGY') && (
                  <div className={styles.CheckboxesDiv}>
                    <span className={styles.title}>
                      MICROBIOLOGY (if the test is not mentioned in the list
                      below, please add in under ‘other lab tests’)
                    </span>
                    <div className={styles.innerCheckboxes}>
                      <CustomCheckbox
                        name="microbiology"
                        label="Cervical culture"
                        value="Cervical culture"
                      />
                      <CustomCheckbox
                        name="microbiology"
                        label="Sputum"
                        value="Sputum"
                      />
                      <CustomCheckbox
                        name="microbiology"
                        label="Stool culture"
                        value="Stool culture"
                      />
                      <CustomCheckbox
                        name="microbiology"
                        label="Stool Ova and Parasites"
                        value="Stool Ova and Parasites"
                      />
                      <CustomCheckbox
                        name="microbiology"
                        label="Throat culture"
                        value="Throat culture"
                      />
                      <CustomCheckbox
                        name="microbiology"
                        label="Urine culture"
                        value="Urine culture"
                      />
                      <CustomCheckbox
                        name="microbiology"
                        label="Vaginal culture"
                        value="Vaginal culture"
                      />
                      <CustomCheckbox
                        name="microbiology"
                        label="Vaginal/Rectal-Group B Strep culture"
                        value="Vaginal/Rectal-Group B Strep culture"
                      />
                    </div>
                  </div>
                )}

                {searchStringChecker('MICROBIOLOGY CHLAMYDIA') && (
                  <div className="w-50">
                    <CustomInput
                      name="chlamydia"
                      label="MICROBIOLOGY CHLAMYDIA ( Specify source )"
                      placeholder="Your answer"
                    />
                  </div>
                )}

                {searchStringChecker('MICROBIOLOGY GONORRHEA') && (
                  <div className="w-50">
                    <CustomInput
                      name="gonorrhea"
                      label="MICROBIOLOGY GONORRHEA ( Specify source )"
                      placeholder="Your answer"
                    />
                  </div>
                )}
                {searchStringChecker('MICROBIOLOGY WOUND') && (
                  <div className="w-50">
                    <CustomInput
                      name="wound"
                      label="MICROBIOLOGY WOUND ( Specify source )"
                      placeholder="Your answer"
                    />
                  </div>
                )}

                {searchStringChecker('Vitamin D') && (
                  <div className={styles.CheckboxesDiv}>
                    <span>Vitamin D</span>
                    <span className={styles.light}>
                      Insured: Meets OHIP eligibility criteria: osteopenia;
                      osteoporosis; rickets; renal disease; malabsorption
                      syndromes; medications affecting vitamin D metabolism
                    </span>
                    <div className={styles.innerCheckboxes}>
                      <CustomCheckbox
                        name="vitamind"
                        label="Vitamin D, 25-hydroxy Insured"
                        value="Vitamin D, 25-hydroxy Insured"
                      />
                      <CustomCheckbox
                        name="vitamind"
                        label="Vitamin D, 25-hydroxy not Insured"
                        value="Vitamin D, 25-hydroxy not Insured"
                      />
                    </div>
                  </div>
                )}
                {searchStringChecker('Add another test') && (
                  <div>
                    <hr />
                    {!options.showTest && (
                      <button
                        className={styles.clearbtn}
                        type="button"
                        onClick={() =>
                          setOptions((old) => ({ ...old, showTest: true }))
                        }
                      >
                        {' '}
                        <img src={images.plus} alt="plus" /> Add another test
                      </button>
                    )}
                    {options.showTest && (
                      <div>
                        <span className={styles.light}>
                          DONOT ADD ANY COMMAS , BRACKETS OR ANY OTHER SPECIAL
                          CHARACTERS Add all investigations in separate lines
                          (press enter to start entering text in a new line)
                        </span>
                        <CustomTextArea
                          name="other"
                          placeholder="Write test name.."
                        />
                      </div>
                    )}
                    <hr />
                  </div>
                )}

                {searchStringChecker('Reason for Requisition') && (
                  <div className="w-50">
                    <CustomInput
                      name="reason"
                      label="Reason for Requisition :"
                    />
                  </div>
                )}

                <div>
                  <hr />
                </div>

                <div className="d-flex justify-content-end">
                  <button
                    type="submit"
                    className={styles.SaveBtn}
                    disabled={FormikProps.isSubmitting}
                  >
                    Save
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default AddModal
